import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { useStaffAuth } from '@/features/auth/useStaffAuth';

/**
 * The admin portal's navigation — one list, reoriented by CSS. Five entries for
 * an `admin_staff`, nine for a `super_admin`.
 *
 * 🔴 IT DOES NOT GROW AHEAD OF THE SCREENS. Canvas `5f` draws seven entries:
 * Overview, Tenants, Devices & SIMs, Plans & billing, Installations, Command
 * console, Team & roles. Three point at nothing that exists — Overview is
 * deferred on a measurement (Mahad, 25 Aug), Installations and Devices & SIMs
 * are slice 30, the command console needs slice 14, and there is no staff
 * management surface. None appears here, disabled or otherwise: a greyed entry
 * promises a feature and dates the promise, the rule slice 09's sidebar earned
 * the hard way. Slice 17e moved this nav from a top strip into the left rail
 * and changed NOTHING about the set.
 *
 * ONE ELEMENT, RESPONSIVE — NOT two lists behind a breakpoint. The first 17e
 * attempt rendered this twice, once in the rail and once in a sub-`lg` top bar,
 * with CSS hiding whichever did not apply. Three existing tests went red with
 * "Found multiple elements with the text: Sign out", and that was NOT a test
 * artefact: jsdom applies no CSS, and neither does a screen reader's landmark
 * list. Two navs sharing an aria-label and two identically-named buttons were
 * genuinely in the DOM. Reorienting ONE list with `lg:` variants removes the
 * duplication rather than hiding it, and removes the drift two sources carry.
 *
 * TYPE AND PADDING COME FROM 24i's EXTRACTED GEOMETRY, NOT FROM THE WEB SHELL.
 * `web/src/app/AppShell.tsx` ships `text-[12.5px]` / `px-3 py-1.5`; the admin
 * canvas agrees with neither. Measured by intersecting the `### Token
 * inventory` blocks of ALL NINE admin artboards (`1s 1t 1u 1v 3d 5f 5g 6i 6j`):
 *
 *   `600 12px 'IBM Plex Sans'`  — 9/9. The only 12-13px type common to all.
 *   `9px 12px`                  — 9/9, as a single compound padding value.
 *
 * Values that LOOK right and are NOT universal, with the counts re-derived
 * after review caught one of them wrong:
 *
 *   `13px` as a BARE value      5/9  (absent from 5f, 5g, 6i, 6j)
 *   `500 13px 'IBM Plex Sans'`  6/9  (absent from 1s, 1v, 3d)
 *   `400 13px 'IBM Plex Sans'`  4/9
 *   `700 13px 'Space Grotesk'`  1/9
 *
 * ⚠️ A SUBSTRING SEARCH FOR "13px" RETURNS 9/9 AND MEANS NOTHING — it matches
 * `500 13px` on the four boards where the bare value is absent. Review reported
 * the bare-value count as inverted on exactly that basis; re-deriving with
 * exact-value matching settled it. Match whole inventory values, not substrings.
 *
 * The real discriminator is stronger than "13px is not universal": NO
 * weight+size pair at 13px reaches 9/9 (6, 4 and 1 above), while `600 12px`
 * does. That is why the nav item is 12px.
 *
 * 🔴 STAFF (slice 17w) IS THE FIRST ENTRY THAT VARIES BY ROLE. Staff management
 * is `super_admin`-only on the server (every route is `requireSuperAdmin`), so
 * an `admin_staff` must not even see the link — a link that 403s on click is
 * the dangling control this codebase rules out. It appears now, and only now,
 * because the screen behind it exists, and only for the role that can use it.
 * This is still "the nav does not grow ahead of the screens"; it is the screen
 * landing, with its control, for the audience that has it.
 *
 * ⚠️ HONEST LIMIT OF THE EVIDENCE: the token inventories are FLAT and
 * de-duplicated — they list every value on the artboard and do NOT say which
 * element each belongs to. So these are the best-evidenced candidates in their
 * band, NOT proof that the sidebar item uses them. The rail WIDTH is different:
 * it comes from the per-element `### Layout skeleton`, so 230px is exact. Do
 * not read this comment as claiming more than that.
 */

// In the slide-in drawer (< lg) the items stack as 44px-tall rows so they stay
// comfortably tappable on a phone (slice 84b bumped 36px → 44px below lg); from
// `lg` they are the rail's compact 12px links with no min height.
const LINK =
  'rounded-lg px-3 py-[9px] text-[12px] font-semibold transition-colors ' +
  'flex min-h-11 items-center lg:block lg:min-h-0';

type NavItem = { to: string; end: boolean; key: string };

const ITEMS: readonly NavItem[] = [
  { to: '/', end: true, key: 'tenants' },
  { to: '/live', end: false, key: 'live' },
  // Slice 18f. STAFF, not super-admin: `GET /admin/vehicles` is `requireStaff`
  // and deliberately carries no IMEI, which is what lets a support hire see the
  // fleet list at all. It ships in the SAME COMMIT as the screen behind it —
  // the rule `fix/09` earned, where a sidebar advertised six routes that were
  // not there.
  { to: '/vehicles', end: false, key: 'vehicles' },
  // Slice 30. STAFF, not super-admin: `GET /admin/installations` is
  // `requireStaff` and carries no IMEI (the tracker is named by model), so a
  // support hire reads the install register. It ships in the SAME COMMIT as the
  // screen behind it. Placed after Vehicles because an install annotates a
  // vehicle's tracker assignment.
  { to: '/installations', end: false, key: 'installations' },
  { to: '/zone-library', end: false, key: 'library' },
  { to: '/plans', end: false, key: 'plans' },
];

// Super-admin only. Kept separate rather than filtered inline so the base set
// stays a plain constant and the role branch is one obvious place.
const SUPER_ADMIN_ITEMS: readonly NavItem[] = [
  // Slice 18d. Same rule as Staff, for the same reason: every inventory route is
  // `requireSuperAdmin`, so an `admin_staff` must not even see the link — one
  // that 403s on click is the dangling control this codebase rules out. The two
  // registers are two entries rather than one, because they are two screens: a
  // combined "Devices & SIMs" label would promise a merged view that does not
  // exist. `1u`'s own title says "devices & SIMs"; its table is devices.
  //
  // Slice 18f. `GET /admin/commissioning` carries IMEIs and is
  // `requireSuperAdmin`, so an `admin_staff` must not even see the link. Placed
  // beside the registers because that is what it is: hardware, before a
  // customer.
  { to: '/commissioning', end: false, key: 'commissioning' },
  // Slice 14c. `GET /admin/command-clearances` carries IMEIs and is
  // `requireSuperAdmin`, so an `admin_staff` must not even see the link. It
  // ships in the SAME COMMIT as the screen behind it, and it is deliberately
  // prominent: a clearance is a temporary accepted-risk state, and the way such
  // a state becomes permanent is that nothing enumerates it.
  { to: '/clearances', end: false, key: 'clearances' },
  { to: '/devices', end: false, key: 'devices' },
  { to: '/sims', end: false, key: 'sims' },
  { to: '/staff', end: false, key: 'staff' },
  // Slice 70b. Super-admin only, same rule as Staff: `GET /admin/whatsapp/messages`
  // is `requireSuperAdminOr404`, so an `admin_staff` must not even see the link —
  // one that 404s on click is the dangling control this codebase rules out. It
  // ships in the SAME COMMIT as the screen behind it. The log spans every tenant's
  // conversations, which is why it is super-admin, not staff.
  { to: '/whatsapp', end: false, key: 'whatsapp' },
];

export function AdminNav({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation();
  const { state } = useStaffAuth();
  const isSuperAdmin =
    state.status === 'authenticated' && state.user.role === 'super_admin';
  const items = isSuperAdmin ? [...ITEMS, ...SUPER_ADMIN_ITEMS] : ITEMS;

  return (
    <nav
      aria-label={t('nav.label')}
      // Below `lg` the drawer stacks the items in a column (gap-1); from `lg`
      // they stack in the rail (lg:gap-0.5). The pre-84b wrapping row is gone —
      // the shell's slide-in drawer, not the nav, is now the mobile container.
      className="flex flex-col gap-1 lg:gap-0.5"
    >
      {items.map(({ to, end, key }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          // Selecting a link closes the drawer on a phone (no-op on desktop,
          // where the drawer is the always-open static rail). Slice 84b.
          onClick={() => onNavigate?.()}
          className={({ isActive }) =>
            [
              LINK,
              isActive
                ? 'bg-raad-primary/15 text-raad-accent'
                : 'text-raad-muted hover:text-raad-text',
            ].join(' ')
          }
        >
          {t(`nav.${key}`)}
        </NavLink>
      ))}
    </nav>
  );
}
